import React, { createContext, useContext, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import * as Sentry from '@sentry/react';
import { UserContext } from '@/components/App';
import { useClientUser } from '@/hooks';
import {
    GET_CORP_DEFAULT_INTEGRATIONS,
    GET_CORP_DOCUMENTS,
    GET_CORP_INTEGRATION_OVERRIDES,
    GET_CORP_PRICING_OVERRIDES,
    GET_CORP_RESERVATION_OVERRIDES,
    GET_CORP_SHIPPER_PARTNERSHIPS,
    GET_CORP_TARIFFS,
    GET_ORG_PROFILE_ASSIGNMENTS,
    GET_ORG_CONTRACTOR_PARTNERSHIPS,
} from './graphql';
import {
    buildAssignmentsByShipper,
    buildCarrierAssetMaps,
    buildContractorRows,
    isOrgFlexCenter,
    MARKETPLACE_KEY,
} from './utils';

export const CorpAdminContext = createContext({});

const CorpAdminStore = ({ organizationId, children }) => {
    const { admin_clients } = useContext(UserContext);
    const { organization_id: clientOrganizationId } = useClientUser();
    // Signed in as a carrier, the client user carries the carrier's org, not the admin's.
    const viewingOrganizationId = organizationId || clientOrganizationId;

    const orgClientIds = useMemo(() => new Set((admin_clients || []).map((c) => c.client_id)), [admin_clients]);

    const orgShipperIds = useMemo(
        () => (admin_clients || []).filter((c) => !!c.user?.roles?.['SHIPPER']).map((c) => c.client_id),
        [admin_clients]
    );

    const allOrgClientIds = useMemo(() => [...orgClientIds], [orgClientIds]);

    const {
        data: integrationData,
        loading: intLoading,
        error: intError,
    } = useQuery(GET_CORP_INTEGRATION_OVERRIDES, {
        variables: { orgShipperIds },
        skip: orgShipperIds.length === 0,
    });

    const {
        data: defaultIntegrationData,
        loading: defaultIntLoading,
        error: defaultIntError,
    } = useQuery(GET_CORP_DEFAULT_INTEGRATIONS, {
        variables: { organization_id: viewingOrganizationId },
        skip: !viewingOrganizationId,
    });

    const {
        data: reservationData,
        loading: reservationLoading,
        error: reservationError,
    } = useQuery(GET_CORP_RESERVATION_OVERRIDES, {
        variables: { orgShipperIds },
        skip: orgShipperIds.length === 0,
    });

    // First-leg defaults live on middle_mile_carrier_id (save.utils remapFirstLegOverride).
    const defaultIntegrationsByCarrier = useMemo(
        () =>
            (defaultIntegrationData?.integration_overrides || []).reduce((byCarrier, override) => {
                const carrierId = override.carrier_id || override.middle_mile_carrier_id;
                (byCarrier[carrierId] ??= []).push(override);

                return byCarrier;
            }, {}),
        [defaultIntegrationData]
    );

    // The server matches reservations on carrier_id alone and reads every type but DISPATCHTRACK as ONWARD.
    const capacityByCarrier = useMemo(
        () =>
            (reservationData?.reservation_overrides || []).reduce((byCarrier, reservation) => {
                const datesFromCarrierSystem = reservation.reservation_type === 'DISPATCHTRACK';
                if (!datesFromCarrierSystem) return byCarrier;

                (byCarrier[reservation.carrier_id] ??= new Set()).add(reservation.shipper_id);

                return byCarrier;
            }, {}),
        [reservationData]
    );

    const {
        data: pricingData,
        loading: priceLoading,
        error: priceError,
    } = useQuery(GET_CORP_PRICING_OVERRIDES, {
        variables: { orgShipperIds, organization_id: viewingOrganizationId },
        skip: !viewingOrganizationId,
    });

    const {
        data: tariffsData,
        loading: tariffsLoading,
        error: tariffsError,
    } = useQuery(GET_CORP_TARIFFS, {
        variables: { client_ids: allOrgClientIds, organization_id: viewingOrganizationId },
        skip: allOrgClientIds.length === 0,
    });

    const {
        data: partnershipData,
        loading: partnershipsLoading,
        error: partnershipsError,
    } = useQuery(GET_CORP_SHIPPER_PARTNERSHIPS, {
        variables: { orgShipperIds },
        skip: orgShipperIds.length === 0,
    });

    const { assetHeavy, assetLight, defaultOverridesByCarrier, flexCenterPricingByContractor } = useMemo(
        () =>
            buildCarrierAssetMaps({
                integrationOverrides: integrationData?.integration_overrides,
                pricingOverrides: pricingData?.pricing_overrides,
                adminClients: admin_clients,
                partnerships: partnershipData?.partnerships,
            }),
        [integrationData, pricingData, admin_clients, partnershipData]
    );

    const orgFlexCenterIds = useMemo(
        () => (admin_clients || []).filter(isOrgFlexCenter).map((c) => c.client_id),
        [admin_clients]
    );

    const {
        data: contractorData,
        loading: contractorsLoading,
        error: contractorsError,
    } = useQuery(GET_ORG_CONTRACTOR_PARTNERSHIPS, {
        variables: { orgFlexCenterIds },
        skip: orgFlexCenterIds.length === 0,
    });

    const contractors = useMemo(() => buildContractorRows(contractorData?.partnerships), [contractorData]);

    const orgCarrierIds = useMemo(() => {
        const contractorIds = contractors.map((contractor) => contractor.client_id);
        const ids = new Set([...Object.keys(assetHeavy), ...Object.keys(assetLight), ...contractorIds]);
        ids.delete(MARKETPLACE_KEY);
        return [...ids];
    }, [assetHeavy, assetLight, contractors]);

    const {
        data: documentsData,
        loading: docsLoading,
        error: docsError,
        refetch: refetchCorpDocs,
    } = useQuery(GET_CORP_DOCUMENTS, {
        variables: { orgCarrierIds, orgShipperIds, organization_id: viewingOrganizationId },
        skip: !viewingOrganizationId || (orgCarrierIds.length === 0 && orgShipperIds.length === 0),
    });

    const { corpDocsByCarrier, rateAddendumsByCarrierShipper } = useMemo(() => {
        const orgDocs = documentsData?.org_documents || [];
        const rates = documentsData?.rate_addendums || [];

        const corpDocsByCarrier = {};
        for (const doc of orgDocs) {
            if (!doc.client_id) continue;
            corpDocsByCarrier[doc.client_id] ??= {};
            // Only one active doc per (carrier, type) by design — last write wins if duplicate.
            corpDocsByCarrier[doc.client_id][doc.type] = doc;
        }

        const rateAddendumsByCarrierShipper = {};
        for (const doc of rates) {
            if (!doc.client_id || !doc.partner_client_id) continue;
            rateAddendumsByCarrierShipper[doc.client_id] ??= {};
            rateAddendumsByCarrierShipper[doc.client_id][doc.partner_client_id] = doc;
        }

        return { corpDocsByCarrier, rateAddendumsByCarrierShipper };
    }, [documentsData]);

    const {
        data: assignmentsData,
        loading: assignmentsLoading,
        error: assignmentsError,
    } = useQuery(GET_ORG_PROFILE_ASSIGNMENTS, {
        variables: { organization_id: viewingOrganizationId },
        skip: !viewingOrganizationId,
        fetchPolicy: 'cache-and-network',
        onError: (e) => {
            console.error(e);
            Sentry.captureException(e);
        },
    });

    const assignmentsByShipper = useMemo(
        () => buildAssignmentsByShipper(assignmentsData?.delivery_profiles),
        [assignmentsData]
    );
    // Until the assignments arrive, a shipper with no entry is unknown, not unassigned.
    const assignmentsUnavailable = !assignmentsData && (assignmentsLoading || !!assignmentsError);

    const tariffs = tariffsData?.tariffs || [];
    const loading =
        intLoading ||
        defaultIntLoading ||
        reservationLoading ||
        priceLoading ||
        tariffsLoading ||
        docsLoading ||
        contractorsLoading ||
        partnershipsLoading;
    const error =
        intError ||
        defaultIntError ||
        reservationError ||
        priceError ||
        tariffsError ||
        docsError ||
        contractorsError ||
        partnershipsError;

    const [notification, setNotification] = useState({});
    // Unsaved "Customize for this shipper" rows, kept here so closing the modal doesn't lose them.
    const [customizeDrafts, setCustomizeDrafts] = useState({});

    return (
        <CorpAdminContext.Provider
            value={{
                state: {
                    assetHeavy,
                    assetLight,
                    defaultOverridesByCarrier,
                    flexCenterPricingByContractor,
                    defaultIntegrationsByCarrier,
                    capacityByCarrier,
                    contractors,
                    integrationOverrides: integrationData?.integration_overrides || [],
                    orgShipperIds,
                    tariffs,
                    loading,
                    error,
                    defaultIntLoading,
                    defaultIntError,
                    admin_clients,
                    notification,
                    corpDocsByCarrier,
                    rateAddendumsByCarrierShipper,
                    assignmentsByShipper,
                    assignmentsUnavailable,
                    customizeDrafts,
                    organizationId: viewingOrganizationId,
                },
                callbacks: { setNotification, refetchCorpDocs, setCustomizeDrafts },
            }}
        >
            {children}
        </CorpAdminContext.Provider>
    );
};

export default CorpAdminStore;
